import { FormEvent, useEffect, useState } from "react";
import { Switch } from "@headlessui/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import usePriceSettings from "../../data/usePriceSettings";
import LoadingWheel from "../shared/LoadingWheel";
import { DropOffPricingType, ICustomerPriceConfiguration } from "../../models/PriceSettings";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import classNames from "../../utils/classNames";

interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description?: string;
}

function Toggle({ checked, onChange, label, description }: ToggleProps) {
  return (
    <Switch.Group as="div" className="flex items-center gap-4">
      <Switch
        checked={checked}
        onChange={onChange}
        className={classNames(
          checked ? "bg-primary-600" : "bg-gray-200",
          "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        )}
      >
        <span
          aria-hidden="true"
          className={classNames(
            checked ? "translate-x-5" : "translate-x-0",
            "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
          )}
        />
      </Switch>
      <span className="flex flex-col">
        <Switch.Label className="text-sm font-medium text-gray-900 cursor-pointer">{label}</Switch.Label>
        {description && <Switch.Description className="text-sm text-gray-500">{description}</Switch.Description>}
      </span>
    </Switch.Group>
  );
}

interface PriceInputProps {
  id: string;
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  help: string;
  error?: string;
}

function PriceInput({ id, label, value, onChange, help, error }: PriceInputProps) {
  return (
    <div className="max-w-xs">
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      <div className="relative mt-1">
        <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-gray-500">£</span>
        <input
          id={id}
          type="number"
          min={0}
          step={1}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? null : Math.max(0, +e.target.value))}
          className={classNames(
            error ? "border-red-300 focus:border-red-500 focus:ring-red-500" : "border-gray-300 focus:border-primary-500 focus:ring-primary-500",
            "block w-full rounded-md pl-7 text-sm shadow-sm"
          )}
        />
      </div>
      <p className={classNames(error ? "text-red-600" : "text-gray-500", "mt-1 text-xs")}>{error ?? help}</p>
    </div>
  );
}

const pricingOptions = [
  { value: DropOffPricingType.PerTonne, name: "Per tonne" },
  { value: DropOffPricingType.Flat, name: "Flat price" },
];

export default function ScrapDropOff() {
  const { priceSettings, updateConfig } = usePriceSettings();
  const { saveHandlers } = useDefaultCRUDHandlers("Drop Off Settings");

  const [priceConfiguration, setPriceConfiguration] = useState<ICustomerPriceConfiguration | null>(null);

  const [doesDropOff, setDoesDropOff] = useState(false);
  const [pricingType, setPricingType] = useState(DropOffPricingType.PerTonne);
  const [pricePerTonne, setPricePerTonne] = useState<number | null>(null);
  const [flatPrice, setFlatPrice] = useState<number | null>(null);
  const [flatIgnoresRules, setFlatIgnoresRules] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    const configuration = priceSettings.data?.data.customerPriceConfiguration;

    if (!priceSettings.isLoading && configuration && !priceConfiguration) {
      setPriceConfiguration(configuration);
      setDoesDropOff(configuration.doesDropOff);
      // Falls back so a configuration from an API without flat drop offs still shows per tonne
      setPricingType(configuration.dropOffPricingType ?? DropOffPricingType.PerTonne);
      setPricePerTonne(configuration.dropOffPricePerTonne);
      setFlatPrice(configuration.dropOffFlatPrice ?? null);
      setFlatIgnoresRules(configuration.dropOffFlatIgnoresRules ?? false);
    }
  }, [priceSettings.data, priceSettings.isLoading]);

  const isFlat = pricingType === DropOffPricingType.Flat;
  const flatPriceError = doesDropOff && isFlat && flatPrice === null ? "Enter a flat price, or switch to per tonne" : undefined;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (flatPriceError) {
      setShowErrors(true);
      return;
    }

    // Both prices are sent whichever is in use, so switching back doesn't lose the other
    updateConfig.mutate(
      {
        ...priceConfiguration!,
        doesDropOff,
        dropOffPricingType: pricingType,
        dropOffPricePerTonne: pricePerTonne,
        dropOffFlatPrice: flatPrice,
        dropOffFlatIgnoresRules: flatIgnoresRules,
      },
      {
        onSuccess: () => saveHandlers.onSuccess(),
        onError: () => saveHandlers.onError(),
      }
    );
  };

  if (!priceConfiguration) return (<div className="bg-white shadow rounded p-4 mt-4 flex relative"><LoadingWheel /></div>);

  return (
    <form onSubmit={onSubmit} className="bg-white shadow rounded p-4 mt-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Drop offs</h2>
          <p className="text-sm text-gray-500">Let customers bring their vehicle to your yard themselves.</p>
        </div>

        <div className="flex items-center gap-6">
          <Toggle
            checked={doesDropOff}
            onChange={setDoesDropOff}
            label={doesDropOff ? "Accepting drop offs" : "Collection only"}
          />
          <ButtonInput isSubmit={true} label="Save" classes="py-[10px]" disabled={updateConfig.isLoading} />
        </div>
      </div>

      {doesDropOff && (
        <div className="mt-4 space-y-5 border-t border-gray-200 pt-4">
          <div>
            <span className="block text-sm font-medium text-gray-700">How do you price drop offs?</span>
            <div className="mt-1 inline-flex rounded-md shadow-sm" role="radiogroup" aria-label="Drop off pricing">
              {pricingOptions.map((option) => {
                const isSelected = option.value === pricingType;

                return (
                  <button
                    key={option.name}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => setPricingType(option.value)}
                    className={classNames(
                      "first:rounded-l-md last:rounded-r-md -ml-px first:ml-0 border px-4 py-2 text-sm font-medium transition-colors duration-200 focus:z-10 focus:outline-none focus:ring-2 focus:ring-primary-500",
                      isSelected
                        ? "relative z-10 bg-primary-600 text-white border-primary-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    )}
                  >
                    {option.name}
                  </button>
                );
              })}
            </div>
          </div>

          {isFlat ? (
            <>
              <PriceInput
                id="dropOffFlatPrice"
                label="Flat drop off price (£)"
                value={flatPrice}
                onChange={setFlatPrice}
                help="Every drop off starts at this price, whatever the vehicle weighs."
                error={showErrors ? flatPriceError : undefined}
              />

              <Toggle
                checked={flatIgnoresRules}
                onChange={setFlatIgnoresRules}
                label="Don't apply any rules to drop offs"
                description="Quote every drop off at exactly the flat price."
              />

              {flatIgnoresRules ? (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 max-w-2xl">
                  <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-700">
                    No rules will be applied to drop offs. Every drop off is quoted at exactly{" "}
                    {flatPrice !== null ? `£${flatPrice}` : "the flat price"}. Specific rules, alloy wheels,
                    starts and drives, salvage and parts lookup prices, vouchers and the Drop Off adjustment are all
                    ignored. Collection quotes are not affected, and motorcycles with a fixed motorcycle price still
                    use that price.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-500 max-w-2xl">
                  Your rules still apply on top of the flat price, the same as they do for collection.
                </p>
              )}
            </>
          ) : (
            <PriceInput
              id="dropOffPricePerTonne"
              label="Drop off price per tonne (£)"
              value={pricePerTonne}
              onChange={setPricePerTonne}
              help="Multiplied by the vehicle's weight. Leave blank to use your postcode prices."
            />
          )}
        </div>
      )}
    </form>
  );
}
